import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { EnvironmentService } from '@galaxy/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { User } from '../../core/user';
import {
  activeCampaignAppIdV2,
  ContactLimit,
  editionIdFromPlanTypeAndContactLimit,
  PlanType,
  planTypeAndContactLimitFromEditionId,
} from '../../marketplace-app/global-brands-constants';
import { ChangeEditionDialogDataInterface, ChangeEditionDialogService } from '../change-edition-dialog.service';
import {
  ACTIVE_CAMPAIGN_CHANGE_EDITION_DIALOG_CONFIG,
  ACTIVE_CAMPAIGN_UPGRADE_EDITION_DIALOG_CONFIG,
} from '../constants';

@Component({
  templateUrl: 'ac-change-edition-dialog.component.html',
  styleUrls: ['ac-change-edition-dialog.component.scss'],
  standalone: false,
})
export class ActiveCampaignChangeEditionDialogComponent implements OnInit {
  PlanType = PlanType;
  ContactLimit = ContactLimit;
  PlanTypeList = [PlanType.Plus, PlanType.Pro, PlanType.Enterprise];
  ContactLimitList = [
    ContactLimit.limit1000,
    ContactLimit.limit2500,
    ContactLimit.limit5000,
    ContactLimit.limit10000,
    ContactLimit.limit25000,
    ContactLimit.limit50000,
    ContactLimit.limit75000,
    ContactLimit.limit100000,
    ContactLimit.limit150000,
    ContactLimit.limit200000,
    ContactLimit.limit250000,
    ContactLimit.limit300000,
    ContactLimit.limit800000,
  ];
  readonly acChangeEditionDialogConfig = ACTIVE_CAMPAIGN_CHANGE_EDITION_DIALOG_CONFIG;
  readonly acUpgradeEditionDialogConfig = ACTIVE_CAMPAIGN_UPGRADE_EDITION_DIALOG_CONFIG;

  currentEditionId: string;
  currentPlanType: PlanType;
  currentContactLimit: ContactLimit;

  selectedPlanType: PlanType;
  selectedContactLimit: ContactLimit;

  hasEmailUsersChecked = true;
  users$: Observable<User[]>;

  confirmationCheckbox = false;
  submitting = false;

  isTrial = false;
  isActiveCampaignV2 = false;

  constructor(
    public dialogRef: MatDialogRef<ActiveCampaignChangeEditionDialogComponent>,
    private dialog: MatDialog,
    private changeEditionDialogService: ChangeEditionDialogService,
    private snackbarService: SnackbarService,
    private environmentService: EnvironmentService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeEditionDialogDataInterface,
  ) {}

  ngOnInit(): void {
    this.users$ = this.changeEditionDialogService.users$;

    this.currentEditionId = this.data.currentEditionId;
    this.isTrial = this.data.isTrial;

    this.isActiveCampaignV2 = this.data.appId === activeCampaignAppIdV2[this.environmentService.getEnvironment()];
    if (!this.isActiveCampaignV2) {
      this.updatePlanTypeAndContactLimitForV1();
    }

    const planTypeAndContactLimit = planTypeAndContactLimitFromEditionId(
      this.currentEditionId,
      this.environmentService.getEnvironment(),
      this.isActiveCampaignV2,
    );
    if (planTypeAndContactLimit) {
      this.currentPlanType = planTypeAndContactLimit.planType;
      this.selectedPlanType = this.currentPlanType;

      this.currentContactLimit = planTypeAndContactLimit.contactLimit;
      this.selectedContactLimit = this.currentContactLimit;
    }
  }

  changeSelectedContactLimit(event: MatRadioChange): void {
    this.selectedContactLimit = event.value;
  }

  planTypeChanged(): void {
    if (
      (this.selectedContactLimit === ContactLimit.limit1000 &&
        this.selectedPlanType !== PlanType.Plus &&
        this.selectedPlanType !== PlanType.Lite) ||
      (this.selectedContactLimit === ContactLimit.limit500 && this.selectedPlanType !== PlanType.Lite)
    ) {
      this.selectedContactLimit = null;
    }
  }

  activateEditionForApp(): void {
    const newEditionId = editionIdFromPlanTypeAndContactLimit(
      this.selectedPlanType,
      this.selectedContactLimit,
      this.environmentService.getEnvironment(),
      this.isActiveCampaignV2,
    );
    if (this.isTrial) {
      // redirects to confirm order step
      this.changeEditionDialogService.handleTrialUpgradeToPaid(this.data.appId, newEditionId);
      this.dialogRef.close();
      return;
    }
    this.submitting = true;
    if (!newEditionId) {
      this.snackbarService.openErrorSnack('Uh-oh. Looks like something went wrong. Please try again.');
      this.submitting = false;
      return;
    }

    this.changeEditionDialogService
      .activateEditionForApp(this.data.appId, this.data.activationId, newEditionId, null, this.hasEmailUsersChecked)
      .pipe(first())
      .subscribe({
        next: (res) => {
          if (res) {
            this.snackbarService.openSuccessSnack('Awesome! Your edition change was submitted successfully.');
            this.dialogRef.close(res);
          } else {
            this.snackbarService.openErrorSnack('Uh-oh. Looks like something went wrong on our end. Please try again.');
          }
        },
        error: () => {
          this.snackbarService.openErrorSnack('Uh-oh. Looks like something went wrong on our end. Please try again.');
        },
      })
      .add(() => {
        this.submitting = false;
      });
  }

  private updatePlanTypeAndContactLimitForV1(): void {
    this.PlanTypeList.unshift(PlanType.Lite);
    this.ContactLimitList.unshift(ContactLimit.limit500);
  }
}
